import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Sparkles, X, CheckCircle2, ExternalLink, Loader2, ChevronDown, ChevronUp, Quote } from 'lucide-react';
import { useOrchestratorContext, clearOrchestratorContext, clearFullOrchestratorContext } from '../../hooks/useOrchestratorContext';
import { useProject } from '../../contexts/ProjectContext';
import { useSmartRouterSessionContext } from '../../hooks/useSmartRouterSessionContext';
import {
  completeOrchestratorStep,
  heartbeatOrchestratorStep,
  OrchestratorCompletePayload,
} from '../../services/smartStartApi';

const HEARTBEAT_INTERVAL_MS = 25_000;

interface Props {
  stepLabel?: string;
  buildCompletionPayload: () => Promise<Omit<OrchestratorCompletePayload, 'token'> | null>
    | Omit<OrchestratorCompletePayload, 'token'> | null;
  canFinish?: boolean;
  disabledReason?: string;
  buildResumeExtra?: () => Record<string, any>;
}

const SmartRouterBanner: React.FC<Props> = ({
  stepLabel,
  buildCompletionPayload,
  canFinish,
  disabledReason,
  buildResumeExtra,
}) => {
  const ctx = useOrchestratorContext();
  const session = useSmartRouterSessionContext();
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { currentProject } = useProject();

  // Auto-dismiss if user switched to a different project than the session project.
  // Reset dismissed when user switches back to the session project.
  useEffect(() => {
    const sessionProjectId = ctx.extraParams?.projectId
      ? parseInt(ctx.extraParams.projectId, 10)
      : null;
    if (!sessionProjectId || !currentProject) return;
    if (currentProject.id !== sessionProjectId) {
      setDismissed(true);
    } else {
      setDismissed(false);
    }
  }, [currentProject?.id, ctx.extraParams?.projectId]);
  const [goalExpanded, setGoalExpanded] = useState(false);
  const buildResumeExtraRef = useRef(buildResumeExtra);
  buildResumeExtraRef.current = buildResumeExtra;

  useEffect(() => {
    if (!ctx.inRouter || ctx.sessionId == null || ctx.stepIndex == null) return;
    let cancelled = false;
    const send = () => {
      if (cancelled) return;
      const extra = typeof buildResumeExtraRef.current === 'function'
        ? buildResumeExtraRef.current() || {}
        : {};
      heartbeatOrchestratorStep(ctx.sessionId!, ctx.stepIndex!, extra).catch(() => {});
    };
    send();
    const id = setInterval(send, HEARTBEAT_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [ctx.inRouter, ctx.sessionId, ctx.stepIndex]);

  if (!ctx.inRouter || dismissed) return null;
  if (ctx.sessionId == null || ctx.stepIndex == null || !ctx.token) return null;

  const finish = async () => {
    try {
      setSubmitting(true);
      const raw = await Promise.resolve(buildCompletionPayload());
      if (!raw) {
        setSubmitting(false);
        return;
      }
      await completeOrchestratorStep(ctx.sessionId!, ctx.stepIndex!, {
        token: ctx.token!,
        artifact: raw.artifact,
        summary: raw.summary || null,
        chat_transcript: raw.chat_transcript,
      });
      toast.success('Finished — returning to Smart Router.');
      clearFullOrchestratorContext();
      window.location.href = ctx.returnTo || `/app/smart-start/${ctx.sessionId}`;
    } catch (err: any) {
      toast.error(`Could not finish step: ${err?.response?.data?.error || err.message || err}`);
      setSubmitting(false);
    }
  };

  const backToRouter = () => {
    const target = ctx.returnTo
      || (ctx.sessionId != null ? `/app/smart-start/${ctx.sessionId}` : null);
    if (!target) return;
    clearFullOrchestratorContext();
    window.location.href = target;
  };

  const disabled = submitting || canFinish === false;

  const resolvedLabel = stepLabel || session.stepLabel;
  const stepPosition =
    session.stepNumber != null && session.stepTotal != null
      ? `Step ${session.stepNumber} of ${session.stepTotal}`
      : null;
  const goal = session.userGoal;

  return (
    <div className="sticky top-2 z-40 mx-2 bg-lavender-deep text-white shadow-md rounded-xl">
      <div className="px-4 py-2.5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 flex-shrink-0" />
          <div className="text-sm min-w-0">
            <span className="font-semibold">Smart Router:</span>{' '}
            <span className="opacity-95">
              {stepPosition ? `${stepPosition} — ` : ''}
              {resolvedLabel || 'Completing a routed step'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={backToRouter}
            className="flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg"
            title="Leave this step in-progress and return to the Smart Router plan view"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Back to plan
          </button>
          <button
            onClick={finish}
            disabled={disabled}
            title={canFinish === false ? (disabledReason || 'Finish the work on this screen first') : undefined}
            className="flex items-center gap-1.5 text-xs bg-white text-lavender-deep font-semibold hover:bg-gray-100 hover:text-lavender-deep active:bg-gray-200 px-3 py-1.5 rounded-lg shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <CheckCircle2 className="w-3.5 h-3.5" />}
            Finish for Smart Router
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-white/10 rounded"
            title="Hide banner"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {goal && (
        <div className="border-t border-white/15 px-4 py-2">
          <button
            onClick={() => setGoalExpanded((v) => !v)}
            className="w-full flex items-start gap-2 text-left"
            title={goalExpanded ? 'Collapse your original request' : 'Show your original request'}
          >
            <Quote className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 opacity-80" />
            <span className="text-xs min-w-0 flex-1">
              <span className="font-semibold opacity-90">Your request:</span>{' '}
              <span className={`opacity-90 ${goalExpanded ? '' : 'line-clamp-1'}`}>{goal}</span>
            </span>
            {goalExpanded
              ? <ChevronUp className="w-3.5 h-3.5 flex-shrink-0 opacity-80" />
              : <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 opacity-80" />}
          </button>
        </div>
      )}
    </div>
  );
};

export default SmartRouterBanner;
